import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
const DESKTOP_MEDIA = '(min-width: 769px)';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#dedupeRenderedNavigation();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Close menu immediately when clicking on any link
    this.addEventListener('click', (event) => {
      if (event.target instanceof HTMLAnchorElement || (event.target instanceof Element && event.target.closest('a'))) {
        this.#deactivate(this.#state.activeItem);
      }
    }, {
      signal: this.#abortController.signal,
    });

    // Button trigger (e.g. Shop By) click → toggle submenu
    this.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element
        ? event.target.closest('button[aria-haspopup]')
        : null;
      if (!trigger) return;
      if (trigger.getAttribute('aria-expanded') === 'true') {
        this.close();
      } else {
        this.activate({ target: trigger });
      }
    }, { signal: this.#abortController.signal });

    // Keyboard: Space toggles any [aria-haspopup] trigger; Escape closes + returns focus
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const trigger = this.#state.activeItem;
        if (trigger) {
          this.close();
          trigger.focus();
        }
        return;
      }
      if (event.key === ' ') {
        const trigger = event.target instanceof Element
          ? event.target.closest('[aria-haspopup]')
          : null;
        if (!trigger) return;
        event.preventDefault();
        if (trigger.getAttribute('aria-expanded') === 'true') {
          this.close();
          trigger.focus();
        } else {
          this.activate({ target: trigger });
        }
      }
    }, { signal: this.#abortController.signal });

    // Close when keyboard focus leaves the nav entirely.
    // Calls #debouncedDeactivate directly (bypasses the desktop Safari FocusEvent guard
    // in the public deactivate() method) so tabbing past the menu closes the submenu.
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.#debouncedActivateHandler.cancel();
        this.#debouncedDeactivate();
      }
    }, { signal: this.#abortController.signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    this.#unlockBodyScroll();
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /** @type {{ locked: boolean; originalOverflow: string }} */
  #scrollLockState = {
    locked: false,
    originalOverflow: '',
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * @returns {boolean}
   */
  #isDesktop() {
    return typeof window !== 'undefined' && window.matchMedia && window.matchMedia(DESKTOP_MEDIA).matches;
  }

  /* Use overflow only so header stays visible (Safari: position:fixed + top:-scrollY hid the header). */
  #lockBodyScroll() {
    if (this.#isDesktop() || this.#scrollLockState.locked) return;
    const body = document.body;
    this.#scrollLockState.originalOverflow = body.style.overflow || '';
    body.style.overflow = 'hidden';
    this.#scrollLockState.locked = true;
  }

  #unlockBodyScroll() {
    if (!this.#scrollLockState.locked) return;
    const body = document.body;
    body.style.overflow = this.#scrollLockState.originalOverflow;
    this.#scrollLockState.locked = false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Shop Menu (schema shop_menu) or Menu item with Shop/Shop By: don't open submenu when empty
    const linkTitle = item.querySelector('.menu-list__link-title') || item.querySelector('.mega-menu__link-title');
    const linkText = linkTitle ? linkTitle.textContent.trim() : '';
    const isShopMenu = item.classList.contains('shop-option-menu');
    const isShopOrShopBy = /^(shop|shop\s*by)$/i.test(linkText.trim());
    const shouldCheckEmpty = isShopMenu || isShopOrShopBy;

    if (shouldCheckEmpty) {
      const submenu = findSubmenu(item);
      const hasContent = submenu && (
        submenu.querySelector('ul') ||
        submenu.querySelector('a') ||
        submenu.textContent.trim().length > 0
      );

      if (!hasContent) {
        this.removeAttribute('data-animating');
        this.dataset.overflowExpanded = 'false';
        this.style.setProperty('--submenu-height', '0px');
        this.style.setProperty('--submenu-opacity', '0');
        if (submenu && submenu instanceof HTMLElement) {
          submenu.style.display = 'none';
        }
        if (item instanceof HTMLElement) {
          item.ariaExpanded = 'false';
        }
        return;
      }
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    item.ariaExpanded = 'true';
    this.ariaExpanded = 'true';
    this.#lockBodyScroll();

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
    
    const columns = document.querySelectorAll('.mega-menu__column');

    columns.forEach(column => {
        const divElement = column.querySelectorAll('div');
        if (divElement) {
            divElement.forEach(divElements => {
                const spanElement = divElements.querySelector('.mega-menu__link .mega-menu__link-title');
            
                if (spanElement && spanElement instanceof HTMLElement) {
                    spanElement.style.fontWeight = 'bold';
                }
            });
        } 
        
    });
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Close the menu (e.g. when backdrop is clicked). Public API for external scripts.
   */
  close() {
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem);
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    // On desktop Safari, ignore blur/focusout so overlay or focus shift doesn't close the menu
    // and cause open/close glitch or leave scroll stuck. Close only on pointerleave.
    if (event && event instanceof FocusEvent && this.#isDesktop()) return;

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#unlockBodyScroll();
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #dedupeRenderedNavigation = () => {
    // Top-level dedupe only. Shop By panel/tab dedupe was deferred to idle and
    // raced with ads — it removed tab DOM and looked like menus "disappearing".
    // Metafield empty-collection filter handles availability; do not strip tabs here.
    this.#dedupeTopLevelMenuItems();
  };

  #dedupeTopLevelMenuItems = () => {
    const menuLinks = this.querySelectorAll('.menu-list__list-item > [ref="menuitem"]');
    const seen = new Set();
    menuLinks.forEach((menuLink) => {
      if (!(menuLink instanceof HTMLElement)) return;
      const title = (menuLink.textContent || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const href = (menuLink.getAttribute('href') || '#').trim();
      const key = `${title}|${href}`;
      const item = menuLink.closest('.menu-list__list-item');
      if (!item) return;
      if (seen.has(key)) {
        item.remove();
        return;
      }
      seen.add(key);
    });
  };

  #resolveShopByPanel = (panelContainer, tabKey) => {
    if (!(panelContainer instanceof HTMLElement) || !tabKey) return null;
    const live = panelContainer.querySelector(`[data-shopby-panel="${tabKey}"]`);
    if (live instanceof HTMLElement) return live;
    const tpl = panelContainer.querySelector(`template[data-shopby-panel-template="${tabKey}"]`);
    const fromTpl = tpl?.content?.querySelector(`[data-shopby-panel="${tabKey}"]`);
    return fromTpl instanceof HTMLElement ? fromTpl : null;
  };

  #dedupeShopByTabsAndPanels = () => {
    const tabList = this.querySelector('.shopby__tabs');
    const panelContainer = this.querySelector('.shopby__panels');
    if (!(tabList instanceof HTMLElement) || !(panelContainer instanceof HTMLElement)) return;

    const tabNodes = Array.from(tabList.querySelectorAll('.shopby__tab-item'));
    if (tabNodes.length === 0) return;

    /** @type {Map<string, { tabItem: HTMLElement, tabEl: HTMLElement | null, panel: HTMLElement | null, template: HTMLTemplateElement | null, score: number, index: number }>} */
    const keepByKey = new Map();
    const removeTargets = [];

    tabNodes.forEach((tabItem, index) => {
      if (!(tabItem instanceof HTMLElement)) return;
      const tabEl = tabItem.querySelector('[data-shopby-tab]');
      const tabKey = tabEl?.getAttribute('data-shopby-tab') || '';
      const panel = this.#resolveShopByPanel(panelContainer, tabKey);
      const template = tabKey
        ? panelContainer.querySelector(`template[data-shopby-panel-template="${tabKey}"]`)
        : null;
      const label = ((tabEl?.textContent || '').trim().replace(/\s+/g, ' ').toLowerCase());
      const href = (tabEl instanceof HTMLAnchorElement ? (tabEl.getAttribute('href') || '#').trim() : '#');
      const key = `${label}|${href}`;
      const score =
        (panel?.querySelectorAll('.shopby__col').length || 0) * 100 +
        (panel?.querySelectorAll('.shopby__col-list li').length || 0);

      const current = {
        tabItem,
        tabEl: tabEl instanceof HTMLElement ? tabEl : null,
        panel,
        template: template instanceof HTMLTemplateElement ? template : null,
        score,
        index,
      };
      const existing = keepByKey.get(key);
      if (!existing) {
        keepByKey.set(key, current);
        return;
      }
      if (current.score > existing.score) {
        removeTargets.push(existing);
        keepByKey.set(key, current);
      } else {
        removeTargets.push(current);
      }
    });

    removeTargets.forEach((target) => {
      target.tabItem.remove();
      // Only remove live panels; template-backed panels are removed with the template.
      if (target.panel?.isConnected) target.panel.remove();
      target.template?.remove();
    });

    const finalTabs = Array.from(tabList.querySelectorAll('[data-shopby-tab]'));
    finalTabs.forEach((t, i) => {
      const isFirst = i === 0;
      t.classList.toggle('is-active', isFirst);
      t.setAttribute('aria-pressed', isFirst ? 'true' : 'false');
      const tabKey = t.getAttribute('data-shopby-tab') || '';
      const panel = panelContainer.querySelector(`[data-shopby-panel="${tabKey}"]`);
      if (panel instanceof HTMLElement) {
        panel.hidden = !isFirst;
      }
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  // Support both: event on list item (li) or on link/span inside (closest finds the link)
  return element.closest('[ref="menuitem"]') || element.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
